import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  newMeetingSchema,
  MeetingFormValues,
} from "../schemas/newMeetingSchema";
import Button from "../../../components/UI/Button";
import { useAddNewMeetingMutation } from "../../../api/calendarApi";
import { useDispatch, useSelector } from "react-redux";
import { addMeeting } from "../../../store/slices/calendarSlice";
import { useGetAllFamiliesQuery } from "../../../api/familyAPI";
import { selectCurrentUser } from "../../../store/slices/authSlice";

interface NewMeetingFormProps {
  onClose: () => void;
}

const NewMeetingForm: React.FC<NewMeetingFormProps> = ({ onClose }) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<MeetingFormValues>({
    resolver: zodResolver(newMeetingSchema),
    mode: "onChange",
  });

  const [addNewMeeting, isLoading] = useAddNewMeetingMutation();
  const { data: families = [] } = useGetAllFamiliesQuery();
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [familyId, setFamilyId] = useState("");
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const mentorId = currentUser?.id || "";

  const onSubmit = async (data: any) => {
    if (!familyId) {
      alert("יש לבחור משפחה מהרשימה");
      return;
    }

    const transformedAttendees = data.attendees
      ? data.attendees.split(",").map((s: string) => s.trim())
      : [];

    const payload = {
      ...data,
      attendees: transformedAttendees,
      mentor_id: mentorId,
      family_id: familyId,
    };

    try {
      await addNewMeeting(payload).unwrap();
      dispatch(
        addMeeting({
          ...payload,
          id: " ",
          family_id: familyId,
          mentor_id: mentorId,
          meeting_number: 0,
          recommendations: "",
          next_meeting_date: "",
          pdf_generated: false,
          email_sent: false,
          financial_data_id: "",
        })
      );
      reset();
      setSearch("");
      setShow(false);
      onClose();
      alert("✅ הפגישה נוספה בהצלחה!");
    } catch (error) {
      alert("אירעה שגיאה בשליחת הפגישה");
    }
  };

  const filtered = families.filter((f) => {
    const fullName = `${f.name_a} ${f.name_b} ${f.family}`.toLowerCase();
    return fullName.includes(search.toLowerCase());
  });

  return (
    <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-lg mt-10">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="relative w-full">
          <input
            type="text"
            {...register("family")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setShow(true)}
            placeholder="בחר משפחה"
            className="w-full border border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg px-4 py-2 shadow-sm transition duration-150 ease-in-out"
          />

          {show && filtered.length > 0 && (
            <ul className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50 animate-fade-in">
              {filtered.map((f) => (
                <li
                  key={f.id}
                  onClick={() => {
                    const fullName = `${f.name_a} ${f.name_b} ${f.family}`;
                    setFamilyId(f.id);
                    setSearch(fullName);
                    setValue("family", fullName, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setShow(false);
                  }}
                  className="px-4 py-2 hover:bg-blue-100 cursor-pointer transition duration-150 ease-in-out"
                >
                  {`${f.name_a} ${f.name_b} ${f.family}`}
                </li>
              ))}
            </ul>
          )}
        </div>
        {errors.family && (
          <p className="text-red-500 text-sm">{errors.family.message}</p>
        )}

        <input
          type="datetime-local"
          {...register("date")}
          className="w-full border p-2 rounded"
        />

        {errors.date && (
          <p className="text-red-500 text-sm">{errors.date.message}</p>
        )}

        <input
          type="text"
          {...register("location")}
          placeholder="מיקום"
          className="w-full border p-2 rounded"
        />

        {errors.location && (
          <p className="text-red-500 text-sm">{errors.location.message}</p>
        )}

        <input
          type="text"
          {...register("attendees")}
          placeholder="משתתפים"
          className="w-full border p-2 rounded"
        />

        {errors.attendees && (
          <p className="text-red-500 text-sm">{errors.attendees.message}</p>
        )}

        <Button
          type="submit"
          className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
        >
          {isLoading ? "שולח..." : "שלח פגישה"}
        </Button>
      </form>
    </div>
  );
};

export default NewMeetingForm;
